import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';

const DisplayLoaderEventId = 'DisplayLoaderEvent';

export function showBlockingLoader({seconds, message, callback}) {
	window.dispatchEvent(
		new CustomEvent(DisplayLoaderEventId, {
			detail: {seconds, message, callback}
		})
	);
}

///Summary:
/// This component listens for a custom event 'DisplayLoaderEvent' (see DisplayLoaderEventId) on the window object.
//  When this event is triggered, it expects the event detail to contain 'seconds', 'callback' (non mandatory), and 'message' properties.
//  It displays a loading indicator for the specified number of seconds, shows the provided message,
//  and then executes the callback function. If a new 'DisplayLoaderEvent' event is triggered while a loader is already active,
//  it will reset the timer and update the message accordingly.

export default function BlockingLoadingIndicator() {
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const timerRef = useRef(null);

	useEffect(() => {
		const handleScheduleCallback = (event) => {
			const {seconds, callback, message} = event.detail || {};

			if (typeof seconds !== 'number' || seconds <= 0) {
				return;
			}

			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}

			setIsLoading(true);
			setMessage(message || '');

			timerRef.current = setTimeout(() => {
				if (typeof callback === 'function') {
					callback();
				}
				timerRef.current = null;
				setIsLoading(false);
				setMessage('');
			}, seconds * 1000);
		};

		window.addEventListener(DisplayLoaderEventId, handleScheduleCallback);

		return () => {
			window.removeEventListener(DisplayLoaderEventId, handleScheduleCallback);
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, []);

	return isLoading ? (
		<StyledBlockingLoader className="StyledBlockingLoader">
			<LoadingIndicator show={isLoading} fullscreen tip={message} size={'lg'} variant="circular" />
		</StyledBlockingLoader>
	) : null;
}

const StyledBlockingLoader = styled.section`
	position: fixed;
	inset: 0;
	z-index: calc(var(--z-topmost-fullscreen) + 1);
	background: var(--neutrals-100);
	opacity: 0.9;
`;
